import type { DataJoinType, FormItemType, SourceType } from './agendaLookups';
import type { Condition } from './agendas';
import type { VisibilityState } from './views';
import type { INTERACTION_TYPE, VIEW_MODE } from './enums.ts';
import type { TemplateType } from './templates.ts';
import type { TranslationName } from './translator';

export type FormItemDefaultValue = {
  formItemId: string | null;
  valueDirect: string | null;
  valueSourceType: string | null;
  valueOffsetDays?: number | null;
} | null;

export type TextStyle = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  color: string;
};

export type FormItemUiDefinition = {
  columnBorderColor?: string;
  attributeColor?: string;
  textStyle?: TextStyle | null;
  icon?: string | null;
  iconColor?: string | null;
  labelLayout?: 'SideBySide' | 'Stacked';
};

export interface FormItem {
  id?: string;
  name: string;
  title: string;
  translatesTitle?: TranslationName[];
  x: string;
  y: string;
  isAuxiliary: boolean;
  definition: FormItemDefinition;
  visibilities?: FormItemVisibility[];
  isActionInput?: boolean;
  isParentInput?: boolean;
  defaultValue?: FormItemDefaultValue;
}

export interface FormItemWithSubtab extends FormItem {
  startTab?: number;
  startSubTab?: number;
  startSectionName?: string;
}

export interface FormItemVisibility {
  id?: string;
  name?: string;
  state?: VisibilityState;
  formItemUiDefinition?: FormItemUiDefinition;
  conditions?: Condition[];
}

export interface FormItemTab {
  number: number;
  name?: string | null;
  description?: string | null;
}

export interface FormItemTabs {
  tab: FormItemTab;
  items: FormItem[];
}

export interface FormItemDefinition {
  type: FormItemType;
  Text?: TextFormItem;
  Number?: NumberFormItem;
  Email?: EmailFormItem;
  PhoneNumber?: PhoneNumberFormItem;
  AutomaticSequence?: AutomaticSequenceFormItem;
  RichText?: RichTextFormItem;
  Computed?: ComputedFormItem;
  Checkbox?: CheckboxFormItem;
  RadioButton?: RadioButtonFormItem;
  radioButton?: RadioButtonFormItem;
  Date?: DateFormItem;
  date?: DateFormItem;
  Button?: ButtonFormItem;
  button?: ButtonFormItem;
  Comment?: CommentFormItem;
  FileUpload?: FileUploadFormItem;
  fileUpload?: FileUploadFormItem;
  Records?: RecordsFormItem;
  RecordsExternal?: RecordsExternalFormItem;
  records?: RecordsFormItem;
  Tables?: TablesFormItem;
  tables?: TablesFormItem;
  Users?: UsersFormItem;
  users?: UsersFormItem;
}

export interface FormItemDefinitionWithButton extends FormItemDefinition {
  button?: ButtonFormItem;
}

/** Flow backend text align. null = not set (FE defaults to Left). */
export enum FlowTextAlign {
  Left = 'Left',
  Center = 'Center',
  Right = 'Right',
}

export interface TextFormItem {
  isRequired: boolean;
  isUnique: boolean;
  maxLength?: number;
  changeBehavior: string;
  labelTextAlign?: FlowTextAlign | null;
  valueTextAlign?: FlowTextAlign | null;
}

export interface NumberFormItem {
  isRequired: boolean;
  isUnique: boolean;
  changeBehavior: string;
  unit?: string | null;
  thousandsSeparator?: 'Space' | 'Dot' | 'Comma' | null;
  mathRound?: 'Arithmetic' | 'Up' | 'Down' | null;
  roundTo?: 'TenThousandths' | 'Thousandths' | 'Hundredths' | 'Tenths' | 'Dozens' | 'Hundreds' | 'Thousands' | null;
}

export interface EmailFormItem {
  isRequired: boolean;
  isUnique: boolean;
  labelTextAlign?: FlowTextAlign | null;
  valueTextAlign?: FlowTextAlign | null;
}

export interface PhoneNumberFormItem {
  isRequired: boolean;
  isUnique: boolean;
  labelTextAlign?: FlowTextAlign | null;
  valueTextAlign?: FlowTextAlign | null;
}

export interface AutomaticSequenceFormItem {
  isRequired: boolean;
  isUnique: boolean;
  startValue: number;
  labelTextAlign?: FlowTextAlign | null;
  valueTextAlign?: FlowTextAlign | null;
}

export interface RichTextFormItem {
  isRequired: boolean;
  isUnique: boolean;
  maxLength: number;
  changeBehavior: string;
  labelTextAlign?: FlowTextAlign | null;
  valueTextAlign?: FlowTextAlign | null;
}

export interface ComputedFormItem {
  isUnique: boolean;
  joinType: DataJoinType;
  params?: ComputedFormItemParam[];
  type: string;
}

export interface ComputedFormItemParam {
  prefix: string | null;
  sourceType: SourceType | null;
  formItemId: string | null;
  referencedFormItemId: string | null;
}

export interface CheckboxFormItem {
  isUnique: boolean;
  isCheckedByDefault: boolean;
  changeBehavior: string;
}

export interface RadioButtonFormItem {
  isRequired: boolean;
  isUnique: boolean;
  changeBehavior: string;
  items: { name: string }[];
}

export interface DateFormItem {
  dateMask: string;
  isRequired: boolean;
  isUnique: boolean;
  changeBehavior: string;
  fromDate: Date | null;
  toDate: Date | null;
  fromSourceType?: SourceType;
  toSourceType?: SourceType;
  fromFormItemId?: string;
  toFormItemId?: string;
}

export interface ButtonFormItem {
  name: string;
  translatesName?: TranslationName[];
  changeBehavior: string;
  showButtonDirectly?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CommentFormItem {}

export interface FileUploadFormItem {
  isRequired: boolean;
  changeBehavior: string;
  maxFilesNumber: string | null;
}

export interface RecordsFormItem {
  isRequired: boolean;
  isUnique: boolean;
  changeBehavior: string;
  isMultiSelect: boolean;
  formItemIdToShow?: string | null;
  agendaId: string | null;
  viewId: string | null;
  params: RecordsViewParam[];
  viewMode: VIEW_MODE;
  interactions: INTERACTION_TYPE[];
  editableWithRecordDetail?: boolean;
}

export interface RecordsViewParam {
  formItemId?: string;
  valueDirect?: string;
  valueSourceType?: SourceType;
  valueFormItemId?: string;
  valueFormItemName?: string;
}

export interface RecordsExternalFormItem {
  isRequired: boolean;
  isUnique: boolean;
  changeBehavior: string;
  isMultiSelect: boolean;
  templateType: TemplateType;
  settings: {
    browse: {
      codeListTemplateId: string | null;
      detailTemplateId: string | null;
      templateRelations: {
        heliosItemId: number;
        flowItemId: string;
      }[];
    };
  };
  viewMode: VIEW_MODE;
  interactions: INTERACTION_TYPE[];
}

export interface TablesFormItem {
  isRequired: boolean;
  isUnique: boolean;
  changeBehavior: string;
  isMultiSelect: boolean;
  columnIdToShow: string;
  tableId: string;
  viewId: string;
  params: TablesViewParam[];
}

export interface TablesViewParam {
  columnId: string;
  valueDirect: string;
  valueSourceType: SourceType;
  valueFormItemId: string;
}

export interface UsersFormItem {
  isRequired: boolean;
  isUnique: boolean;
  changeBehavior: string;
  isMultiSelect: boolean;
  roleIds: string[];
}

export interface UserItem {
  userId: string;
  fullName: string;
}
